<template>
    <v-layout
            row
            justify-center
            align-center
    >
        <!-- GLOBAL INTERNAL FILE INPUT HANDLER -->
        <input
                :id="options.id"
                :ref="options.id"
                type="file"
                style="display: none"
                :name="options.id"
                :accept="options.accept"
                :capture="options.capture"
                :disabled="options.disabled"
                :multiple="options.multiple"
                @change="onFilePicked"
                @click="cleanFileEvent"
        >

        <!-- GLOBAL INTERNAL DIALOG FILE INPUT HANDLER -->
        <v-dialog
                v-model="uploadFileDialog"
                persistent
                scrollable
                :fullscreen="isMobile()"
                max-width="1000px"
        >
            <v-card class="y-scrollable" :class="{'isMobile()': 'rounded' }">
                <v-card-text class="pa-2">
                    <v-layout row wrap justify-center align-center>
                        <v-flex shrink>
                            <v-icon left>
                                mdi-upload
                            </v-icon>
                        </v-flex>
                        <v-flex grow>
                            <span class="subheading"><strong>{{ $t('globalControls.fileInput.uploadFiles') }}</strong></span>
                        </v-flex>
                        <v-flex shrink>
                            <v-btn
                                    flat
                                    small
                                    icon
                                    @click="uploadFileDialog = false"
                            >
                                <v-icon>mdi-close-circle</v-icon>
                            </v-btn>
                        </v-flex>
                    </v-layout>
                </v-card-text>
                <v-card-text class="pa-2">
                    <v-flex v-if="showHeader" xs12 class="px-2 pb-2">
                        <div v-if="options.data.title" class="headline">
                            {{ options.data.title }}</strong>
                        </div>
                        <div v-if="options.data.subtitle" class="subheading grey--text">
                            <strong>{{ options.data.subtitle }}</strong>
                        </div>
                    </v-flex>
                    <v-flex style="height: 200px; max-height: 400px;" @click="pickFile()">
                        <form :ref="this.formId" :class="formClass" class="form-inline text-center">
                            <span class="input-form-description">{{ $t('globalControls.fileInput.dragAndDrop') }}</span>
                        </form>
                    </v-flex>
                    <v-flex>
                        <FileInputDetails
                                :options="options"
                                :files="uploadedFiles.files"
                        ></FileInputDetails>
                    </v-flex>
                </v-card-text>
                <v-card-text class="pa-2">
                    <v-layout row wrap justify-end>
                        <v-btn color="blue darken-1" flat
                                :disabled="fileInLoading[this.options.id] !== undefined && Object.keys(fileInLoading[this.options.id]).length > 0"
                                @click="onUpload()"
                        >
                            {{ options.data.upload }}
                        </v-btn>
                    </v-layout>
                </v-card-text>
            </v-card>
        </v-dialog>

        <!-- -->
        <v-dialog
                v-model="detailsFileDialog"
                persistent
                scrollable
                max-width="800px"
        >
            <FileInputDetails
                    v-if="options.details.show === true && options.details.position === 'dialog' && uploadedFiles.files.length > 0"
                    :options="options"
                    :files="uploadedFiles.files"
            />
        </v-dialog>

        <!-- ****** INPUT TYPES ****** -->
        <!-- TEXT TYPE -->
        <v-flex v-if="options.formType === 'text'" xs12>
            <v-layout row wrap>
                <v-flex v-if="showHeader()" xs12 py-3 px-2>
                    <div v-if="options.data.title" class="headline">
                        <strong>{{ options.data.title }}</strong>
                    </div>
                    <div v-if="options.data.subtitle" class="subheading grey--text">
                        <strong>{{ options.data.subtitle }}</strong>
                    </div>
                </v-flex>
                <v-flex xs12 sm8 md6 lg7>
                    <v-flex>
                        <v-btn
                                v-if="options.details.position === 'dialog'"
                                flat
                                small
                                icon
                                @click="detailsFileDialog === true"
                        >
                            <v-icon>mdi-format-list-checks</v-icon>
                        </v-btn>

                        <v-text-field
                                v-model="uploadedFiles.names"
                                :label="$t('globalControls.fileInput.selectFile')"
                                readonly
                                prepend-icon="attach_file"
                                @click="pickFile"
                        >

                        </v-text-field>
                    </v-flex>
                </v-flex>
                <v-flex xs12 sm2 md3 lg3>
                    <v-select
                            v-model="opCoValue"
                            :items="opCo"
                            label="OpCo"
                    ></v-select>
                </v-flex>
                <v-flex xs12 sm2 md3 lg2>
                    <v-btn
                            block
                            class="rounded white--text"
                            :color="options.data.buttonColor"
                            :disabled="fileInLoading[this.options.id] !== undefined && Object.keys(fileInLoading[this.options.id]).length > 0"
                            @click="onUpload()"
                    >
                        {{ options.data.upload }}
                        <template v-slot:loader>
              <span class="white--text">Loading...
              </span>
                            <span class="white--text custom-loader">
                <v-icon light>cached</v-icon>
              </span>
                        </template>
                    </v-btn>
                </v-flex>
                <v-flex
                        v-if="options.details.show === true && options.details.position === 'bottom' && uploadedFiles.files.length > 0"
                        xs12
                >
                    <FileInputDetails
                            :options="options"
                            :files="uploadedFiles.files"
                    />
                </v-flex>
            </v-layout>
        </v-flex>

        <!-- BUTTON TYPE -->
        <v-flex
                v-if="options.formType === 'button'"
                xs12
        >
            <v-btn
                    block
                    class="rounded white--text"
                    :color="options.data.buttonColor"
                    @click="uploadFileDialog = true"
            >
                {{ options.data.buttonTitle }}
            </v-btn>
        </v-flex>

        <!-- ICON TYPE -->
        <v-flex
                v-if="options.formType === 'icon'"
                xs12
        >
            <v-btn
                    flat
                    icon
                    color="primary"
                    @click="uploadFileDialog = true"
            >
                <v-icon>mdi-upload</v-icon>
            </v-btn>
        </v-flex>
    </v-layout>
</template>


<script>
    import {mapGetters, mapState,} from "vuex";

    import FileService from "@/helpers/FileService";
    import AppService from "@/helpers/AppService";
    import AlertService from "@/helpers/AlertService";

    import FileInputDetails from "@/components/Inputs/FileInputDetails";

    export default {
        name: "FileInput",
        components: {
            FileInputDetails,
        },
        props: ["options", "additionalData", "onUploadCallback",],
        data() {
            return {
                opCoValue:"Italy",
                opCo: ['Italy','Germany'],
                uploadFileDialog: false,
                detailsFileDialog: false,
                dragAndDropCapable: false,
                formClass: "box",
            };
        },
        computed: {
            ...mapState({}),
            ...mapGetters("FileInputState", {
                fileInputs: "getFileInputs",
                fileInLoading: "getFileInLoading",
            }),
            formId() {
                return this.options.id + "_form";
            },
            uploadedFiles() {
                if (this.fileInputs[this.options.id] !== undefined) {
                    return this.fileInputs[this.options.id];
                } else {
                    return {
                        names: "",
                        files: [],
                    };
                }
            },
        },
        mounted() {
            // Determine if drag and drop functionality is capable in the browser
            this.dragAndDropCapable = AppService.determineDragAndDropCapable();

            //If drag and drop capable, then we continue to bind events to our elements.
            if (this.dragAndDropCapable) {
                // Listen to all of the drag events and bind an event listener to each for the fileform.
                ["drag", "dragstart", "dragend", "dragover", "dragenter", "dragleave", "drop",].forEach(function (evt) {
                    // For each event add an event listener that prevents the default action
                    //(opening the file in the browser) and stop the propagation of the event (so
                    // no other elements open the file in the browser)
                    this.$refs[this.formId].addEventListener(evt, function (e) {
                        e.preventDefault();
                        e.stopPropagation();
                    }.bind(this), false);
                }.bind(this));

                // Add an event listeners to the form
                this.$refs[this.formId].addEventListener("dragover", function (e) {
                    this.formClass = "box box__is-dragover";
                }.bind(this));
                this.$refs[this.formId].addEventListener("dragenter", function (e) {
                    this.formClass = "box box__is-dragover";
                }.bind(this));
                this.$refs[this.formId].addEventListener("dragend", function (e) {
                    this.formClass = "box";
                }.bind(this));
                this.$refs[this.formId].addEventListener("dragleave", function (e) {
                    this.formClass = "box";
                }.bind(this));
                this.$refs[this.formId].addEventListener("drop", function (e) {
                    if (FileService.checkAvailability()) {
                        // Capture the files from the drop event and add them to our local file array.
                        this.formClass = "box";
                        let readFiles = FileService.readFiles(e);
                        let fileToAdd = {
                            refId: this.options.id,
                            multiple: this.options.multiple,
                            files: readFiles,
                        };
                        this.$store.dispatch("FileInputState/addFilesInput", fileToAdd);
                    } else {
                        let alertOptions = {
                            type: "error",
                            message: "The File APIs are not fully supported in this browser.",
                        };
                        AlertService.createAlert(alertOptions);
                    }
                }.bind(this));
            }
        },
        methods: {
            getOpCo(){
                return this.opCoValue;
            },
            isMobile() {
                return this.$vuetify.breakpoint.name === "xs";
            },
            cleanFileEvent(event) {
                event.target.value = '';
            },
            pickFile() {
                if (FileService.checkAvailability()) {
                    this.$refs[this.options.id].click();
                } else {
                    let alertOptions = {
                        type: "error",
                        message: "The File APIs are not fully supported in this browser.",
                    };
                    AlertService.createAlert(alertOptions);
                }

            },
            onFilePicked(evt) {
                let readFiles = FileService.readFiles(evt, this.options.id);
                let fileToAdd = {
                    refId: this.options.id,
                    multiple: this.options.multiple,
                    files: readFiles,
                };
                if (this.options.multiple === false) {
                    this.$store.dispatch("FileInputState/setFilesInput", fileToAdd);
                } else {
                    this.$store.dispatch("FileInputState/addFilesInput", fileToAdd);
                }
            },
            onUpload() {
                this.uploadFileDialog = false;
                let uploadedFileCopy = this.uploadedFiles;
                let _this = this;
                let promise = new Promise(function (resolve, reject) {
                    if (_this.additionalData !== undefined || _this.additionalData != null) {
                        _this.onUploadCallback(uploadedFileCopy, _this.additionalData);
                    } else {
                        _this.onUploadCallback(uploadedFileCopy);
                    }
                    resolve(true);
                });

                return promise.then(function (success) {
                        let emptyFiles = {
                            refId: _this.options.id,
                            multiple: _this.options.multiple,
                            files: [],
                        };
                        _this.$store.dispatch("FileInputState/setFilesInput", emptyFiles);
                    }
                );
            },
            showHeader() {
                return (this.options.data.title !== undefined && this.options.data.title !== "") ||
                    (this.options.data.subtitle !== undefined && this.options.data.subtitle !== "");
            },
        },
    };
</script>

<style lang="scss" scoped>
    .input-form-description {
        position: relative;
        top: 45%;
    }

    .box {
        display: block;
        background-color: white;
        outline: 2px dashed black;
        outline-offset: -10px;
        text-align: center;
        height: 100%;
        border-radius: 4px;
    }

    .box__dragndrop,
    .box__uploading,
    .box__success,
    .box__error {
        display: none;
    }

    .box__has-advanced-upload.box__dragndrop {
        display: inline;
    }

    .box.box__is-dragover {
        background-color: #F2F5FA;
        outline: 2px dashed grey;
        outline-offset: -20px;
    }
</style>
